import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera } from "lucide-react";
import { api, client } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/lib/auth";
import { PageTitle, Card, SectionTitle, Takeaway } from "@/components/shared/primitives";
import { Avatar } from "@/components/shared/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function MyProfile() {
  const { member, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [photoPath, setPhotoPath] = useState(null);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = () => api.get("/auth/me").then((r) => {
    setForm({ name: r.data.name || "", designation: r.data.designation || "", practice_area: r.data.practice_area || "",
              phone: r.data.phone || "", bio: r.data.bio || "", location: r.data.location || "" });
    setPhotoPath(r.data.photo_path || null);
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.patch("/me", form); toast.success("Profile saved."); refresh && refresh(); }
    catch (e) { toast.error(e.message); }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await client.post("/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(res.data.data.photo_path);
      toast.success("Photo updated.");
      refresh && refresh();
    } catch { toast.error("Upload failed. Use a JPG, PNG, WEBP or GIF."); }
  };

  if (!form) return null;

  return (
    <div>
      <PageTitle testid="my-profile-page">My profile</PageTitle>
      <Takeaway>Your firm manages your role and team. You can edit the rest.</Takeaway>

      <Card className="p-6 mb-6">
        <div className="flex items-center gap-5">
          <div className="relative">
            <Avatar photoPath={photoPath} name={form.name} size={80} testid="my-avatar" />
            <button onClick={() => fileRef.current?.click()} data-testid="upload-photo-btn"
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: "var(--brand-navy)", color: "var(--brand-silky)" }} title="Change photo">
              <Camera size={15} strokeWidth={1.5} />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="photo-input" />
          </div>
          <div>
            <div className="font-display text-[22px] font-bold text-nb-ink">{form.name}</div>
            <div className="text-[14px] text-nb-ink2">{ROLE_LABELS[member?.role]}{member?.seniority ? ` · ${member.seniority}` : ""}</div>
          </div>
        </div>
      </Card>

      <Card className="p-6" data-testid="profile-form">
        <SectionTitle className="mb-4">Details</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div><Label className="text-[13px]">Full name</Label><Input data-testid="pf-name" value={form.name} onChange={set("name")} className="mt-1" /></div>
          <div><Label className="text-[13px]">Designation</Label><Input data-testid="pf-designation" value={form.designation} onChange={set("designation")} className="mt-1" placeholder="e.g. Associate" /></div>
          <div><Label className="text-[13px]">Practice area</Label><Input value={form.practice_area} onChange={set("practice_area")} className="mt-1" placeholder="e.g. Litigation" /></div>
          <div><Label className="text-[13px]">Office</Label><Input value={form.location} onChange={set("location")} className="mt-1" /></div>
          <div><Label className="text-[13px]">Phone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1" /></div>
          <div><Label className="text-[13px]">Email (managed by firm)</Label><Input value={member?.email || ""} disabled className="mt-1" style={{ background: "var(--bg-sunken)" }} /></div>
          <div className="md:col-span-2"><Label className="text-[13px]">Team (managed by firm)</Label><Input value={member?.team_id ? "Assigned by your firm" : "Unassigned"} disabled className="mt-1" style={{ background: "var(--bg-sunken)" }} /></div>
          <div className="md:col-span-2"><Label className="text-[13px]">Short bio</Label><Textarea rows={3} value={form.bio} onChange={set("bio")} className="mt-1" /></div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={save} data-testid="save-profile" className="rounded-[10px] bg-nb-navy text-nb-cream font-semibold">Save changes</Button>
        </div>
      </Card>
    </div>
  );
}
